import React, { useRef, useCallback, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import Slider from '@mui/material/Slider';

import LibraryBooks from '@mui/icons-material/LibraryBooks';
//import HomeIcon from '@mui/icons-material/Home';
//import ShuffleIcon from '@mui/icons-material/Shuffle';
import PlayIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import TimerIcon from '@mui/icons-material/AvTimer';
import PrevLineIcon from '@mui/icons-material/NavigateBefore';
import PrevToolPathIcon from '@mui/icons-material/FirstPage';
import NextLineIcon from '@mui/icons-material/NavigateNext';
import NextToolPathIcon from '@mui/icons-material/LastPage';
import useMediaQuery from '@mui/material/useMediaQuery';

import pad from '../util/pad';

const PREFIX = 'ControlsOverlay';

const classes = {
  toolbar: `${PREFIX}-toolbar`,
  overlay: `${PREFIX}-overlay`,
  button: `${PREFIX}-button`,
  blink: `${PREFIX}-blink`,
  left: `${PREFIX}-left`,
  center: `${PREFIX}-center`,
  right: `${PREFIX}-right`,
  break: `${PREFIX}-break`,
  sliderTime: `${PREFIX}-sliderTime`,
  sliderContainer: `${PREFIX}-sliderContainer`,
  timeSliderContainer: `${PREFIX}-timeSliderContainer`,
  timeIconContainer: `${PREFIX}-timeIconContainer`,
  timeIcon: `${PREFIX}-timeIcon`,
  timeMultiplierLabel: `${PREFIX}-timeMultiplierLabel`
};

const StyledContainer = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.toolbar}`]: theme.mixins.toolbar,

  [`& .${classes.overlay}`]: {
    zIndex: theme.zIndex.drawer + 3,
    position: "absolute",
    left: 0,
    bottom: 50,
    right: 0,
    marginLeft: "auto",
    marginRight: "auto",
    width: "670px",
    maxWidth: "100%",
    textAlign: "center",
    verticalAlign: "middle",
    backgroundColor: "rgba(100,100,100,.75)",
    borderRadius: 5,
    color: "white"
  },
  [`& .${classes.blink}`]: {
    animation: "blinkButton 2s infinite"
  },
  "@keyframes blinkButton": {
    "0%": { backgroundColor: "rgba(0,0,0,0)" },
    "50%": { backgroundColor: "rgba(3, 177,136,1)" },
    "100%": { backgroundColor: "rgba(0,0,0,0)" }
  },

  [`& .${classes.button}`]: {
    color: "#dddddd"
  },

  [`& .${classes.left}`]: {
    float: "left",
    width: "5%",
    textAlign: "left"
  },

  [`& .${classes.center}`]: {
    float: "left",
    width: "55%",
    textAlign: "right"
  },

  [`& .${classes.right}`]: {
    float: "left",
    width: "40%",
    marginBottom: 0,
    textAlign: "right"
  },

  [`& .${classes.break}`]: {
    lineHeight: 0,
    clear: "both"
  },

  [`& .${classes.sliderTime}`]: {
    float: "left",
    width: 50,
    marginLeft: 15,
    fontSize: 10,
    lineHeight: "32px",
    verticalAlign: "middle"
  },

  [`& .${classes.sliderContainer}`]: {
    float: "left",
    width: "calc(100% - 95px)",
    marginLeft: 15,
    marginRight: 15,
  },

  [`& .${classes.timeSliderContainer}`]: {
    width: "calc(100% - 100px)",
    float: "right",
    marginRight: 15
  },

  [`& .${classes.timeIconContainer}`]: {
    float: "right",
    lineHeight: "32px",
    cursor: "pointer"
  },

  [`& .${classes.timeIcon}`]: {
    color: "#dddddd",
    verticalAlign: "middle"
  },

  [`& .${classes.timeMultiplierLabel}`]: {
    fontSize: "10px",
    marginRight: 5
  }
}));

const enterDelay = 500;

function ControlsOverlay(props) {
  const {
    visible,
    alwaysVisible,
    paused,
    running,
    value,
    minValue,
    maxValue,
    timeMultiplierValue,
    currentLine,
    currentTime,
    fadeTime, 
    visibleTimeout,
    isolateSlider,
    minLine,
    maxLine,
    onShow,
    onHide,
    onPlay,
    onPause,
    onPrevious,
    onNext,
    onPreviousToolPath,
    onNextToolPath,
    onTogglePane,
    onSlider,
    onTimeSlider
  } = props;

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const timeoutID = useRef();
  const resetTimeout = useCallback(() => {
    if(onHide) {
      if(timeoutID && timeoutID.current) {
        window.clearTimeout(timeoutID.current);
        timeoutID.current = null;
      }

      timeoutID.current = setTimeout(() => {
        onHide();
      }, visibleTimeout || 2000);
    }
  }, [ visibleTimeout, onHide, timeoutID ]);
  
  const handleMouseMove = useCallback((e) => {
    if(!e.buttons) {
      if(!visible && onShow) {
        onShow();
      }

      resetTimeout();
    }
  }, [ visible, onShow, resetTimeout ]);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    }
  }, [ handleMouseMove ]);

  const handlePlay = useCallback((e) => {
    if(onPlay) {
      onPlay(e);
    }
    resetTimeout();
  }, [ onPlay, resetTimeout ]);

  const handlePause = useCallback((e) => {
    if(onPause) {
      onPause(e);
    }
    resetTimeout();
  }, [ onPause, resetTimeout ]);

  const handlePrevious = useCallback((e) => {
    if(onPrevious) {
      onPrevious(e);
    }
    resetTimeout();
  }, [ onPrevious, resetTimeout ]);

  const handleNext = useCallback((e) => {
    if(onNext) {
      onNext(e);
    }
    resetTimeout();
  }, [ onNext, resetTimeout ]);

  const handleNextToolPath = useCallback((e) => {
    if(onNextToolPath) {
      onNextToolPath(currentLine);
    }
    resetTimeout();
  }, [ onNextToolPath, resetTimeout, currentLine ]);

  const handlePreviousToolPath = useCallback((e) => {
    if(onPreviousToolPath) {
      onPreviousToolPath(currentLine);
    }
    resetTimeout();
  }, [ onPreviousToolPath, resetTimeout, currentLine ]);

  const handleTogglePane = useCallback((e) => {
    if(onTogglePane) {
      onTogglePane(e);
    }
    resetTimeout();
  }, [ onTogglePane, resetTimeout ]);

  const handleOnSlider = useCallback((e, v) => {
    if(onSlider) {
      if(isolateSlider) {
        onSlider({ percentage: v, minLine, maxLine });
      } else {
        onSlider(v);
      }
    }
    resetTimeout();
  }, [ onSlider, resetTimeout, minLine, maxLine, isolateSlider ]);

  const handleResetTimeSlider = useCallback((e) => {
    if(onTimeSlider) {
      onTimeSlider(1);
    }
    resetTimeout();
  }, [ onTimeSlider, resetTimeout ]);

  const seconds = Math.floor(currentTime)%60;
  const minutes = Math.floor(currentTime/60)%60;
  const hours = Math.floor(currentTime/60/60);
  const currentTimeString = pad(hours, 2) + ":" + pad(minutes, 2) + ":" + pad(seconds, 2);

  const timePower = 1000;
  const timeSliderValue = timeMultiplierValue <= 1 ? .5*timeMultiplierValue : .5*Math.log(timeMultiplierValue)/Math.log(timePower)+.5; // make time slider a logarithmic scale

  const handleTimeSliderChange = useCallback((e,v) => {
    if(onTimeSlider) {
      if(v <= .5) {
        onTimeSlider(2*v);
      } else {
        onTimeSlider(Math.pow(timePower, 2*(v-.5)))
      }
    }
    resetTimeout();
  }, [ onTimeSlider, timePower, resetTimeout ]);

  return (
  <StyledContainer>
    <Fade in={visible || alwaysVisible} timeout={fadeTime || 500}>
      <div className={classes.overlay}>
      <div>
        <div className={classes.left}>
          <Tooltip title="Toggle Probing Pane" enterDelay={enterDelay} placement="top" disableFocusListener={true}>
            <span>
            <IconButton onClick={handleTogglePane} size="large"><LibraryBooks className={classes.button}/></IconButton>
            </span>
          </Tooltip>
        </div>
        <div className={classes.center}>
          <Tooltip title="Previous Tool Path" enterDelay={enterDelay} placement="top" disableFocusListener={true}>
            <span>
            <IconButton onClick={handlePreviousToolPath} size={ isSmall ? "small" : "medium"}><PrevToolPathIcon className={classes.button}/></IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Previous Line" enterDelay={enterDelay} placement="top" disableFocusListener={true}>
            <span>
            <IconButton onClick={handlePrevious} size={ isSmall ? "small" : "medium"}><PrevLineIcon className={classes.button}/></IconButton>
            </span>
          </Tooltip>
          { 
            running ? (paused 
          ? 
            <Tooltip title="Play" enterDelay={enterDelay} placement="top" disableFocusListener={true}>
              <span>
              <IconButton onClick={handlePlay} size={ isSmall ? "small" : "medium" }><PlayIcon className={classes.button}/></IconButton>
              </span>
            </Tooltip>
          : 
            <Tooltip title="Pause" enterDelay={enterDelay} placement="top" disableFocusListener={true}>
              <span>
              <IconButton onClick={handlePause} size={ isSmall ? "small" : "medium" }><PauseIcon className={classes.button}/></IconButton>
              </span>
            </Tooltip>)
            : null
          }
          <Tooltip title="Next Line" enterDelay={enterDelay} placement="top" disableFocusListener={true}>
            <span>
            <IconButton onClick={handleNext} size={ isSmall ? "small" : "medium" }><NextLineIcon className={classes.button}/></IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next Tool Path" enterDelay={enterDelay} placement="top" disableFocusListener={true}>
            <span>
            <IconButton onClick={handleNextToolPath} size={ isSmall ? "small" : "medium" }><NextToolPathIcon className={classes.button}/></IconButton>
            </span>
          </Tooltip>
        </div>
        <div className={classes.right}>
          <div className={classes.timeSliderContainer}>
            <Slider size="small" sx={ { color: "rgb(216, 216, 216)" }} step={.001} min={0} max={1} value={timeSliderValue} onChange={handleTimeSliderChange}/>
          </div>
          <div className={classes.timeIconContainer}>
            <Tooltip title="Time Scale" enterDelay={enterDelay} placement="top" disableFocusListener={true}>
              <TimerIcon onClick={handleResetTimeSlider} className={classes.timeIcon}/> 
            </Tooltip><span className={classes.timeMultiplierLabel}>{ timeMultiplierValue.toFixed(2) }</span>
          </div>
        </div>
        </div>
        <br className={classes.break}/>
        <div className={classes.sliderControls}>
          <div className={classes.sliderTime}>{ currentTimeString }</div>
          <div className={classes.sliderContainer}>
          <Slider size="small" sx={ { color: "rgb(216, 216, 216)" }} step={.001} min={minValue} max={maxValue} value={value} onChange={handleOnSlider}/>
          </div>
        </div>
      </div>
    </Fade>
  </StyledContainer>
  );
}

export default (ControlsOverlay);
